import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import useFinishFlow, { FLOWS } from 'utilities/navigation/flow'
import { useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import Header from 'components/headers/withGreenBack'
import DotsLoading from 'components/loadings/dotsLoading'
import { faFull, toman } from 'utilities/helper/format'
import {
    createCashboxShareGroup,
    updateCashboxShareGroup,
} from 'services/cashbox'
import {
    Card,
    Field,
    MoneyInput,
    Notice,
    PrimaryButton,
    SecondaryButton,
    TextInput,
    apiMessage,
    isValidPhone,
    normalizePhone,
} from 'modules/cashbox/ui'
import {
    CASHBOX_KEY,
    useCashbox,
    useCashboxMembers,
    useShareGroupView,
} from 'modules/cashbox/hooks'

interface Row {
    phone: string
    amount: number
}

/**
 * گروه خرده‌سهم — several people splitting ONE share between them.
 *
 * The invariant the backend cares about is that the residues add up to exactly
 * `share_value`; anything else leaves the box unable to draw. That sum is
 * shown live here and the save button stays disabled until it matches, so the
 * broken state is unreachable from this screen rather than merely rejected.
 *
 * On update the posted list REPLACES the group's membership — anyone removed
 * from the rows is detached — which is why the form is prefilled with the
 * group's current members rather than starting empty.
 */
export default function CashboxShareGroupPage() {
    const { id, groupId } = useParams()
    const finishFlow = useFinishFlow(FLOWS.cashboxShareGroup)
    const queryClient = useQueryClient()

    const { cashbox, isLoading } = useCashbox(id)
    const { data, isLoading: membersLoading } = useCashboxMembers(id)

    const shareValue = cashbox?.period?.share_value ?? 0
    const { groupViews } = useShareGroupView(
        data?.memberships,
        data?.share_groups,
        shareValue
    )

    const [name, setName] = useState('')
    const [rows, setRows] = useState<Array<Row>>([
        { phone: '', amount: 0 },
        { phone: '', amount: 0 },
    ])
    const [prefilled, setPrefilled] = useState(false)

    const existing = groupId
        ? groupViews.find((view) => String(view.group.id) === groupId)
        : undefined

    useEffect(() => {
        if (!groupId || prefilled || !existing) return
        setName(existing.group.name ?? '')
        setRows(
            existing.members.length > 0
                ? existing.members.map((member) => ({
                      phone: member.phone_number ?? '',
                      amount: member.share_residue,
                  }))
                : [{ phone: '', amount: 0 }]
        )
        setPrefilled(true)
    }, [groupId, existing, prefilled])

    const total = rows.reduce((sum, row) => sum + (row.amount || 0), 0)
    const filledRows = rows.filter((row) => row.phone.trim() !== '')
    const phones = filledRows.map((row) => normalizePhone(row.phone))
    const hasDuplicate = new Set(phones).size !== phones.length
    const allPhonesValid = filledRows.every((row) => isValidPhone(row.phone))
    const allAmountsPositive = filledRows.every((row) => row.amount > 0)

    const problems: Array<string> = []
    if (filledRows.length < 2)
        problems.push('یک گروه خرده‌سهم حداقل دو عضو دارد')
    if (!allPhonesValid) problems.push('شماره موبایل همه‌ی اعضا معتبر نیست')
    if (hasDuplicate) problems.push('یک شماره دو بار وارد شده است')
    if (!allAmountsPositive) problems.push('سهم هر عضو باید بیشتر از صفر باشد')
    if (total !== shareValue)
        problems.push(
            `مجموع سهم‌ها باید دقیقاً ${toman(shareValue)} باشد (اکنون ${toman(
                total
            )})`
        )

    const canSave = problems.length === 0

    const save = useMutation({
        mutationFn: () => {
            const payload = {
                id: id!,
                name: name.trim(),
                group_members: filledRows.map((row) => ({
                    phone_number: normalizePhone(row.phone),
                    share_residue: row.amount,
                })),
            }
            return groupId
                ? updateCashboxShareGroup({
                      ...payload,
                      shareGroupId: Number(groupId),
                  })
                : createCashboxShareGroup(payload)
        },
        onSuccess: () => {
            // An unbalanced group is the activation blocker managers cannot see
            // from the box screen, so both halves of this pair feed
            // `cashbox_activation_blocked`. The group NAME is free text and
            // stays out of it.
            posthog.capture('cashbox_share_group_saved', {
                cashbox_id: id,
                group_id: groupId ? Number(groupId) : null,
                members_count: filledRows.length,
                is_balanced: total === shareValue,
                is_edit: Boolean(groupId),
            })
            toast.success(groupId ? 'گروه به‌روزرسانی شد' : 'گروه ساخته شد')
            queryClient.invalidateQueries(CASHBOX_KEY(id))
            queryClient.invalidateQueries(['cashbox-members', id])
            finishFlow(`/cashbox/${id}/members`)
        },
        onError: (error: any) => {
            posthog.capture('cashbox_share_group_save_failed', {
                cashbox_id: id,
                group_id: groupId ? Number(groupId) : null,
                members_count: filledRows.length,
                is_balanced: total === shareValue,
                is_edit: Boolean(groupId),
                status: error?.response?.status,
            })
            toast.error(apiMessage(error))
        },
    })

    if (isLoading || membersLoading) return <DotsLoading />

    return (
        <div className="bg-[#FAFAFA] min-h-screen pb-8">
            <div className="bg-white">
                <Header
                    title={groupId ? 'ویرایش گروه خرده‌سهم' : 'گروه خرده‌سهم'}
                />
            </div>

            <div className="px-4 pt-3">
                <Notice>
                    چند نفر با هم یک سهم برمی‌دارند. مجموع سهم اعضا باید دقیقاً
                    برابر ارزش هر سهم ({toman(shareValue)}) باشد. اگر این گروه
                    برنده شود، وام به نسبت سهم هرکس بین اعضا تقسیم می‌شود.
                </Notice>

                <div className="mt-4">
                    <Field label="نام گروه">
                        <TextInput
                            value={name}
                            maxLength={50}
                            placeholder="مثلاً خانواده رضایی"
                            onChange={(event) => setName(event.target.value)}
                        />
                    </Field>
                </div>

                {rows.map((row, index) => (
                    <Card key={index} className="mb-2">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-text-secondary">
                                عضو {faFull.format(index + 1)}
                            </span>
                            {rows.length > 1 && (
                                <button
                                    type="button"
                                    className="text-xs text-[#B3261E]"
                                    onClick={() =>
                                        setRows((prev) =>
                                            prev.filter((_, i) => i !== index)
                                        )
                                    }
                                >
                                    حذف
                                </button>
                            )}
                        </div>
                        <Field
                            label="شماره موبایل"
                            error={
                                row.phone && !isValidPhone(row.phone)
                                    ? 'شماره موبایل معتبر نیست'
                                    : undefined
                            }
                        >
                            <TextInput
                                inputMode="tel"
                                value={row.phone}
                                invalid={Boolean(
                                    row.phone && !isValidPhone(row.phone)
                                )}
                                onChange={(event) =>
                                    setRows((prev) =>
                                        prev.map((item, i) =>
                                            i === index
                                                ? {
                                                      ...item,
                                                      phone: event.target.value,
                                                  }
                                                : item
                                        )
                                    )
                                }
                            />
                        </Field>
                        <Field label="سهم این عضو">
                            <MoneyInput
                                value={row.amount}
                                onChange={(amount) =>
                                    setRows((prev) =>
                                        prev.map((item, i) =>
                                            i === index
                                                ? { ...item, amount }
                                                : item
                                        )
                                    )
                                }
                            />
                        </Field>
                    </Card>
                ))}

                <SecondaryButton
                    className="h-10 mb-3"
                    onClick={() =>
                        setRows((prev) => [...prev, { phone: '', amount: 0 }])
                    }
                >
                    + افزودن عضو به گروه
                </SecondaryButton>

                <Card className="mb-3">
                    <div className="flex justify-between items-baseline">
                        <span className="text-sm text-text-secondary">
                            مجموع سهم اعضا
                        </span>
                        <span
                            className={`text-sm font-bold ${
                                total === shareValue
                                    ? 'text-[#4CA632]'
                                    : 'text-[#B3261E]'
                            }`}
                        >
                            {toman(total)}
                        </span>
                    </div>
                    <div className="flex justify-between items-baseline mt-1">
                        <span className="text-sm text-text-secondary">
                            ارزش هر سهم
                        </span>
                        <span className="text-sm text-black">
                            {toman(shareValue)}
                        </span>
                    </div>
                    {total !== shareValue && (
                        <p className="text-xs text-text-secondary mt-2">
                            {total < shareValue
                                ? `${toman(shareValue - total)} کم است`
                                : `${toman(total - shareValue)} اضافه است`}
                        </p>
                    )}
                </Card>

                {problems.length > 0 && (
                    <Notice tone="warn">
                        {problems.map((problem) => (
                            <span key={problem}>
                                • {problem}
                                <br />
                            </span>
                        ))}
                    </Notice>
                )}

                <div className="mt-4">
                    <PrimaryButton
                        disabled={!canSave || save.isLoading}
                        onClick={() => save.mutate()}
                    >
                        {save.isLoading
                            ? 'در حال ذخیره…'
                            : groupId
                            ? 'ذخیره تغییرات'
                            : 'ساخت گروه'}
                    </PrimaryButton>
                </div>
            </div>
        </div>
    )
}
